import { customAlphabet } from 'nanoid'
import { sync, call } from 'vuex-pathify'

import AddToCart from '../graphql/mutations/AddToCart'
import RemoveFromCart from '../graphql/mutations/RemoveFromCart'
import UpdateCart from '../graphql/mutations/UpdateCart'
import EmptyCart from '../graphql/mutations/EmptyCart'
import RevertCart from '../graphql/mutations/RevertCart'
import SubmitOrder from '../graphql/mutations/SubmitOrder'
import VariantQuery from '../graphql/queries/Variant'

const cartid = customAlphabet('2346789ABCDEFGHJKLMNPQRTUVWXYZabcdefghijkmnpqrtwxyz', 36)

let cartUpdating = false

export default {
  computed: {
    cartId: sync('shoppingCart/id'),
    cartStale: sync('shoppingCart/stale'),
    cartData: sync('shoppingCart/data'),
    cartPhone: sync('shoppingCart/address@phone'),
    orderToken: sync('shoppingCart/token')
  },
  methods: {
    addCartItem: call('shoppingCart/addItem'),
    removeCartItem: call('shoppingCart/removeItem'),
    updateCartData: call('shoppingCart/update'),
    clearCartData: call('shoppingCart/clear'),

    notify(text, options) {
      const title = this.$t('eshop.added')
      const message = this.$t(text)

      this.$notify({ message, title, type: 'success', ...options })
    },
    createCart() {
      if (!this.cartId) {
        this.cartId = cartid()
      }
    },
    async mutate(mutation, { onSuccess, onError, ...data }) {
      try {
        const result = await this.$graphql.mutate({
          mutation: mutation,
          clientId: 'eshop',
          variables: {
            input: { data: { cartId: this.cartId, ...data } }
          }
        })

        if (!result) {
          onError && onError.call(null, {})
        }

        if (result.info.status == 'error') {
          onError && onError.call(null, result)
        } else {
          onSuccess && onSuccess.call(null, result)
        }
      } catch (e) {
        console.error(`Cart Error: ${e}`)
        onError && onError.call(null, {})
      }
    },
    async addToCart({ message, ...options }) {
      this.createCart()

      const onSuccess = ({ data }) => {
        this.addCartItem(data)
        this.$emit('add-to-cart', data)

        if (message) {
          this.notify('eshop.addedToCart')
        }
      }

      await this.mutate(AddToCart, { onSuccess, ...options })
    },
    async removeFromCart({ message, ...options }) {
      if (!this.cartId) return

      const onSuccess = ({ data }) => {
        this.removeCartItem(data)
        this.$emit('remove-from-cart', data)

        if (message) {
          this.notify('eshop.removedFromCart')
        }
      }

      await this.mutate(RemoveFromCart, { onSuccess, ...options })
    },
    async updateCart(options) {
      if (!this.cartId) return
      if (cartUpdating) return

      cartUpdating = true

      const onSuccess = ({ data }) => {
        this.updateCartData(data)
      }

      await this.mutate(UpdateCart, { onSuccess, ...options  })
      cartUpdating = false
    },
    async emptyCart(options) {
      if (!this.cartId) return

      const onSuccess = () => {
        this.clearCartData()
      }

      await this.mutate(EmptyCart, { onSuccess, ...options  })
    },
    async revertCart(options) {
      if (!this.cartId) return

      const onSuccess = ({ data }) => {
        this.updateCartData(data)
      }

      await this.mutate(RevertCart, { onSuccess, ...options  })
    },
    async submitOrder(options) {
      if (!this.cartId) return

      const onSuccess = () => {
        this.clearCartData()
      }

      await this.mutate(SubmitOrder, { onSuccess, ...options  })
    },
    async addToCartWithCheck({ onFail, ...options }) {
      const notify = (message, callback) => {
        this.notify( `eshop.${message}`, {
          title: this.$t('eshop.notAdded'),
          type: 'error'
        })

        callback && callback.call(null)
      }

      try {
        const { data } = await this.$graphql.query({
          clientId: 'eshop',
          query: VariantQuery,
          variables: { where:  { id: options.variantId } }
        })

        if (data.orderable == false) {
          return notify('notOrderable', onFail)
        }

        if (!this.isOrderable(data, data.id, options.quantity)) {
          return notify('noStockAvailable', onFail)
        }

        await this.addToCart(options)
      } catch (e) {
        notify('addFailed')
      }
    },
    availableStock(data, id) {
      const items = this.cartData.items || []
      const cart  = items.find(({ variantId }) => variantId == id)
      const stock = data ? (data.totalOnHand || 0) : 0

      return cart ? stock - cart.quantity : stock
    },
    isOrderable(data, variantId, quantity) {
      if (!data || data.totalOnHand == null) return true

      const stock = this.availableStock(data, variantId)

      if (data.orderable == false) {
        return false
      } else if (stock < quantity && data.backorderable == true) {
        return true
      }

      return stock >= quantity
    }
  }
}
